/**
 * LVC simulation transport: the browser talks to Firestore directly.
 *
 * Upload writes a virtual agent's telemetry onto that real user's `skiers/{uid}` document,
 * which is what the goggles app and buddy maps read. A simulated skier is meant to be
 * indistinguishable from a real one, so what a run writes stands after it ends; nothing is
 * reverted. Writes still carry `telemetry.source` and `telemetry.simulationId` so a reader
 * that cares can tell where the data came from. Download follows each live agent's
 * `skiers/{uid}` so their puck tracks real movement.
 *
 * Why the client is allowed to do this: the live rule for /skiers/{userId} is
 *   allow get:           if request.auth != null;
 *   allow list:          if false;
 *   allow write, delete: if request.auth != null && (request.auth.uid == userId || isRekkieEmail());
 * and the backend path this replaces (backend/services/simulationManager.js) wrote with the
 * *caller's* own ID token, so Firestore evaluated the same rule. Two consequences carried
 * into the code below: reads are per document because `list` is denied, and the client SDK
 * refreshes its own token, so long runs no longer die at the one-hour mark.
 *
 * Plan: plans/lvc-revamp/eng-plan.md.
 */

import { arrayRemove, arrayUnion, doc, getDoc, onSnapshot, runTransaction, writeBatch } from 'firebase/firestore';
import { db } from './firebase';

/** Force a write this often even when the agent has not moved, so consumers can tell a
 *  parked skier from a dead feed. */
export const HEARTBEAT_MS = 10000;

/** Coordinates compare to ~1 cm; the rest are scalars where a smaller change is noise. */
const LAT_LON_EPSILON = 1e-7;
const SCALAR_EPSILON = 0.01;

const isMissing = (value) => value === null || value === void 0;

const near = (a, b, epsilon) => {
  if (isMissing(a) || isMissing(b)) return a === b;
  return Math.abs(a - b) <= epsilon;
};

/**
 * True when two positions are the same reading. Used to skip writes that would change
 * nothing but the timestamp (a `Wait` behavior otherwise writes the same point every second).
 * @param {{lat:number, lon:number, altitude?:number, speed?:number, heading?:number}|null} a
 * @param {{lat:number, lon:number, altitude?:number, speed?:number, heading?:number}|null} b
 */
export const isSamePosition = (a, b) => {
  if (!a || !b) return false;
  return near(a.lat, b.lat, LAT_LON_EPSILON)
    && near(a.lon, b.lon, LAT_LON_EPSILON)
    && near(a.altitude ?? 0, b.altitude ?? 0, SCALAR_EPSILON)
    && near(a.speed ?? 0, b.speed ?? 0, SCALAR_EPSILON)
    && near(a.heading ?? 0, b.heading ?? 0, SCALAR_EPSILON);
};

/**
 * The dot-notation field map written for one agent. Same fields the backend wrote, plus
 * `source` and `simulationId` so anything reading `skiers/{uid}` can tell a simulated
 * position from a real one.
 *
 * @param {Object} position - { lat, lon, altitude, speed, heading, simTimestamp }
 * @param {Object|null} stats - { maxVelocitymps, totVerticalMeters, timestamp }
 * @param {string} simulationId
 * @param {Date} [now]
 * @returns {Object} field map for a Firestore update
 */
export const buildTelemetryPatch = (position, stats, simulationId, now = new Date()) => {
  const patch = {
    updatedAt: now,
    'telemetry.coordinate': { latitude: position.lat, longitude: position.lon },
    'telemetry.altitude': position.altitude ?? 0,
    'telemetry.heading': position.heading ?? 0,
    'telemetry.horizAccuracy': position.accuracy ?? 0,
    'telemetry.speed': position.speed ?? 0,
    'telemetry.timestamp': now,
    'telemetry.source': 'lvc',
    'telemetry.simulationId': simulationId,
  };
  if (typeof position.simTimestamp === 'number' && Number.isFinite(position.simTimestamp)) {
    patch['telemetry.simTimestamp'] = position.simTimestamp;
  }
  if (stats) {
    patch['stats.maxVelocitymps'] = stats.maxVelocitymps ?? 0;
    patch['stats.totVerticalMeters'] = stats.totVerticalMeters ?? 0;
    patch['stats.lastUpdate'] = stats.timestamp ?? 0;
  }
  return patch;
};

/**
 * The apps map a solo skier to the in-memory id `riding-solo`, which must never reach
 * Firestore. Treat null, a missing field and that sentinel as "not in a group".
 * See SkiGroupMembership.md.
 */
export const RIDING_SOLO = 'riding-solo';

export const normalizeGroupId = (value) =>
  (!value || value === RIDING_SOLO ? null : value);

/**
 * Decides which agents need writing this second.
 * @param {Array<{uid:string, position:Object, stats:Object|null}>} agents
 * @param {Map<string, {position:Object, at:number}>} lastSent - mutated by uploadTick
 * @param {number} nowMs
 * @param {number} heartbeatMs
 * @returns {Array<{uid:string, position:Object, stats:Object|null}>}
 */
export const selectAgentsToWrite = (agents, lastSent, nowMs, heartbeatMs = HEARTBEAT_MS) =>
  agents.filter(({ uid, position }) => {
    if (!position || isMissing(position.lat) || isMissing(position.lon)) return false;
    const previous = lastSent.get(uid);
    if (!previous) return true;
    if (!isSamePosition(position, previous.position)) return true;
    return nowMs - previous.at >= heartbeatMs;
  });

/**
 * Write every changed agent in one batch. Firestore counts each document separately for
 * billing, but a batch is one round trip and one atomic commit instead of N requests.
 *
 * @param {Array<{uid:string, position:Object, stats:Object|null}>} agents
 * @param {Object} options
 * @param {string} options.simulationId
 * @param {Map<string, {position:Object, at:number}>} options.lastSent - carried across calls
 * @param {number} [options.heartbeatMs]
 * @param {number} [options.nowMs]
 * @returns {Promise<{written:number, uids:string[]}>}
 */
export const uploadTick = async (agents, { simulationId, lastSent, heartbeatMs = HEARTBEAT_MS, nowMs = Date.now() }) => {
  const due = selectAgentsToWrite(agents, lastSent, nowMs, heartbeatMs);
  if (due.length === 0) return { written: 0, uids: [] };

  const now = new Date(nowMs);
  const batch = writeBatch(db);
  due.forEach(({ uid, position, stats }) => {
    batch.update(doc(db, 'skiers', uid), buildTelemetryPatch(position, stats, simulationId, now));
  });
  await batch.commit();

  due.forEach(({ uid, position }) => lastSent.set(uid, { position: { ...position }, at: nowMs }));
  return { written: due.length, uids: due.map((a) => a.uid) };
};

/**
 * Map a `skiers/{uid}` document to the shape the LVC page consumes. Mirrors what
 * getLiveAgentData used to return, minus the polling and the isNew bookkeeping that
 * onSnapshot makes unnecessary.
 */
export const toLiveAgentData = (data) => {
  if (!data) return null;
  const telemetry = data.telemetry || {};
  const coordinate = telemetry.coordinate;
  const lat = coordinate?.latitude ?? coordinate?._lat ?? null;
  const lon = coordinate?.longitude ?? coordinate?._long ?? null;
  if (lat === null || lon === null) return null;

  const stats = data.stats || {};
  const toDate = (value) => (value?.toDate ? value.toDate() : value ?? null);
  return {
    lat,
    lon,
    altitude: telemetry.altitude ?? 0,
    heading: telemetry.heading ?? 0,
    accuracy: telemetry.horizAccuracy ?? 0,
    speed: telemetry.speed ?? 0,
    timestamp: toDate(telemetry.timestamp),
    updatedAt: toDate(data.updatedAt),
    source: telemetry.source ?? null,
    skiGroup: normalizeGroupId(data.skiGroup),
    stats: {
      maxVelocitymps: stats.maxVelocitymps ?? 0,
      totVerticalMeters: stats.totVerticalMeters ?? 0,
      lastUpdate: stats.lastUpdate ?? 0,
    },
  };
};

/**
 * Follow one live agent's document. One listener per uid: the rules deny `list`, so a
 * collection query would be rejected.
 * @param {string} uid
 * @param {(data: Object|null) => void} onData
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribeLiveAgent = (uid, onData, onError) => onSnapshot(
  doc(db, 'skiers', uid),
  (snap) => onData(snap.exists() ? toLiveAgentData(snap.data()) : null),
  (error) => {
    console.error(`[lvc] live agent ${uid} listener failed:`, error);
    if (onError) onError(error);
  }
);

/**
 * Current ski group of each uid, read one document at a time because `list` is denied.
 * @param {string[]} uids
 * @returns {Promise<Map<string, string|null>>} uid -> group id, or null for solo
 */
export const readSkiGroups = async (uids) => {
  const entries = await Promise.all(uids.map(async (uid) => {
    try {
      const snap = await getDoc(doc(db, 'skiers', uid));
      return [uid, snap.exists() ? normalizeGroupId(snap.data().skiGroup) : null];
    } catch (error) {
      console.error(`[lvc] could not read ski group for ${uid}:`, error);
      return [uid, null];
    }
  }));
  return new Map(entries);
};

/**
 * Move several virtual agents into one ski group, as a single transaction.
 *
 * Shaped as the bulk move in SkiGroupMembership.md: one arrayUnion on the destination and one
 * arrayRemove per distinct previous group, rather than a write per skier, because every agent
 * targets the same group document and Firestore sustains about one write per second per
 * document. The current group of each skier is read inside the transaction rather than taken
 * from a listener, so a skier who changes group in the app mid-run makes Firestore retry
 * instead of being removed from the wrong group.
 *
 * Never creates a group: the live user owns it and joins it in the app.
 *
 * @param {string[]} uids - virtual agent uids to move
 * @param {string} groupId - destination group, must already exist
 * @returns {Promise<{moved: string[], alreadyIn: string[], missing: string[]}>}
 */
export const MAX_TRANSACTION_WRITES = 500;

export const moveAgentsToGroup = async (uids, groupId) => {
  const targets = [...new Set(uids)].filter(Boolean);
  if (!groupId || targets.length === 0) return { moved: [], alreadyIn: [], missing: [] };

  return runTransaction(db, async (transaction) => {
    const groupRef = doc(db, 'skigroups', groupId);
    const groupSnap = await transaction.get(groupRef);
    if (!groupSnap.exists()) throw new Error(`Ski group "${groupId}" does not exist`);

    // All reads first: a Firestore transaction rejects a read issued after a write. They run
    // together rather than one await at a time, so a scenario's agents cost one round trip.
    const skierSnaps = await Promise.all(targets.map((uid) => transaction.get(doc(db, 'skiers', uid))));
    const currentGroups = new Map();
    const missing = [];
    targets.forEach((uid, i) => {
      const snap = skierSnaps[i];
      if (!snap.exists()) missing.push(uid); // update() on a missing doc fails the transaction
      else currentGroups.set(uid, normalizeGroupId(snap.data().skiGroup));
    });

    const previousGroupIds = [...new Set([...currentGroups.values()].filter((id) => id && id !== groupId))];
    const previousSnaps = await Promise.all(previousGroupIds.map((id) => transaction.get(doc(db, 'skigroups', id))));
    const previousExists = new Map(previousGroupIds.map((id, i) => [id, previousSnaps[i].exists()]));

    const moved = [];
    const alreadyIn = [];
    currentGroups.forEach((current, uid) => {
      if (current === groupId) alreadyIn.push(uid);
      else moved.push(uid);
    });
    if (moved.length === 0) return { moved, alreadyIn, missing };

    // One write for the destination, one per previous group, one per skier. Splitting across
    // transactions would give up atomicity and could leave skiers half-moved, so refuse
    // instead and let the operator run it in smaller scenarios.
    const writeCount = 1 + previousGroupIds.length + moved.length;
    if (writeCount > MAX_TRANSACTION_WRITES) {
      throw new Error(
        `Moving ${moved.length} agents needs ${writeCount} writes, over Firestore's ${MAX_TRANSACTION_WRITES} per transaction`
      );
    }

    const now = new Date();
    transaction.update(groupRef, { buddies: arrayUnion(...moved), updatedAt: now });

    previousGroupIds.forEach((id) => {
      const leaving = moved.filter((uid) => currentGroups.get(uid) === id);
      // A group that was deleted underneath the skier is skipped; update() would fail the
      // whole transaction. The skier still gets its recentSkiGroups entry below.
      if (leaving.length > 0 && previousExists.get(id)) {
        transaction.update(doc(db, 'skigroups', id), { buddies: arrayRemove(...leaving), updatedAt: now });
      }
    });

    moved.forEach((uid) => {
      const previous = currentGroups.get(uid);
      const patch = { skiGroup: groupId, updatedAt: now };
      // Dotted path, so the rest of the recentSkiGroups map survives.
      if (previous) patch[`recentSkiGroups.${previous}`] = now;
      transaction.update(doc(db, 'skiers', uid), patch);
    });

    return { moved, alreadyIn, missing };
  });
};
